import React, { useState, useEffect } from 'react';
import { automationAPI, userAPI, taskAPI, emailTemplatesAPI, emailAPI } from '../../services/api';
import ConditionTreeBuilder, { blankGroup } from './ConditionTreeBuilder';
import { PROJECT_TYPES } from '../../constants/projectTypes';
import { WORK_SECTIONS } from '../../constants/workSections';
import './automationBuilder.css';

// Conditions are stored in two shapes. Most live rules keep a ConditionGroup at
// the TOP LEVEL ({logic, conditions, groups}); the rest nest it under
// advancedFilters. The editor only ever read advancedFilters, so a top-level rule
// opened with an EMPTY condition tree — and anything then built in that empty
// editor was ignored on save, because ParseTriggerConditions checks for a
// top-level "logic" first and returns before it ever looks at advancedFilters.
// The rule kept firing on conditions nobody could see, and the person who "fixed"
// it had every reason to think they had.
//
// Normalising on load collapses that to one shape. The legacy scalar keys go with
// it: with a top-level tree present the backend returned early and never read
// them, so they are inert today, and carrying them across would turn dead data
// into live AND conditions that could narrow a rule that currently works.
const TOP_LEVEL_GROUP_KEYS = ['logic', 'conditions', 'groups'];
const INERT_LEGACY_KEYS = [
    'toStatus', 'fromStatus', 'taskTitle',
    'projectTypes', 'excludeProjectTypes', 'excludeSalesRepIds',
];

const normalizeTriggerConditions = (parsed) => {
    if (!parsed || typeof parsed !== 'object') return {};
    if (!Object.prototype.hasOwnProperty.call(parsed, 'logic')) return parsed;

    const hoisted = {
        logic: parsed.logic || 'AND',
        conditions: parsed.conditions || [],
        groups: parsed.groups || [],
    };

    const next = { ...parsed };
    TOP_LEVEL_GROUP_KEYS.forEach((k) => delete next[k]);
    INERT_LEGACY_KEYS.forEach((k) => delete next[k]);

    // Overwrites any existing advancedFilters on purpose — the backend was
    // ignoring it in favour of the top-level tree, so the tree is what this rule
    // actually does.
    next.advancedFilters = hoisted;
    return next;
};

// Mirrors ShouldTriggerStatusAutomation on the backend, which refuses to fire a
// status rule unless its conditions reference newStatus somewhere.
const groupReferencesNewStatus = (group) => {
    if (!group || typeof group !== 'object') return false;
    if ((group.conditions || []).some((c) => c && c.field === 'newStatus')) return true;
    return (group.groups || []).some(groupReferencesNewStatus);
};

const AutomationBuilder = ({ isOpen, onClose, editingAutomation = null }) => {
    const [automation, setAutomation] = useState({
        name: '',
        description: '',
        isActive: true,
        triggerTypes: [], // Now an array for multiple selection
        triggerConditions: {},
        actions: [],
        delayDays: null,
        delayMinutes: null,
        triggerDateField: '',        // for DateRelative
        triggerDateOffsetMinutes: 0  // signed: negative = before the date
    });
    
    const [users, setUsers] = useState([]);
    const [existingTaskTitles, setExistingTaskTitles] = useState([]);
    const [emailTemplates, setEmailTemplates] = useState([]);
    const [sendAsOptions, setSendAsOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [validationErrors, setValidationErrors] = useState([]);
    const [currentStep, setCurrentStep] = useState(1);

    const triggerTypes = [
        { value: 'ProjectCreation', label: 'Project Created', icon: '📁', description: 'Fires when a new project is created' },
        { value: 'ProjectStatusChange', label: 'Status Change', icon: '🔄', description: 'Fires when project status changes' },
        { value: 'TaskCompletion', label: 'Task Completed', icon: '✅', description: 'Fires when a specific task is completed' },
        { value: 'DateRelative', label: 'Date-Relative', icon: '📅', description: 'Fires relative to a job date (e.g. 5 min before Start, 0 min after End)' },
        { value: 'ProjectUpdated', label: 'Job Updated (once)', icon: '✏️', description: 'Fires once when a job field matches (e.g. Standing Seam / Priority Customer set)' },
        { value: 'WorkOrderChange', label: 'Work Order Changed', icon: '🧾', description: 'Fires when a work order is created/updated (use Work Order Status condition)' },
        { value: 'ContactChange', label: 'Contact Changed', icon: '👤', description: 'Fires when a contact is created/updated (use Contact Lead Source condition)' }
    ];

    // Date fields a DateRelative trigger can fire off.
    const dateRelativeFields = [
        { value: 'ScheduledStartDate', label: 'Scheduled Start Date' },
        { value: 'CompletionDate', label: 'Completion / End Date' },
        { value: 'MaterialDeliveryDate', label: 'Material Delivery Date' },
        { value: 'EstimateDate', label: 'Estimate Date' },
        { value: 'ContractSignedDate', label: 'Contract Signed Date' },
        { value: 'InvoiceDueDate', label: 'Invoice Due Date' },
        { value: 'StatusChangedAt', label: 'Last Status Change' },
    ];

    const projectStatuses = [
        { value: 'Lead', label: 'Lead' },
        { value: 'WriteEstimate', label: 'Write Estimate' },
        { value: 'PendingSignature', label: 'Pending Signature' },
        { value: 'SignedContract', label: 'Signed Contract' },
        { value: 'PendingStartDate', label: 'Pending Start Date' },
        { value: 'JobsInProgress', label: 'Jobs In Progress' },
        { value: 'JobCompleted', label: 'Job Completed' },
        { value: 'PendingPayments', label: 'Pending Payments' },
        { value: 'Cancelled', label: 'Cancelled' },
        { value: 'LostFollowUp', label: 'Lost - Follow Up' },
        { value: 'PaidClosed', label: 'Paid / Closed' },
        { value: 'SendWarranty', label: 'Send Warranty' }
    ];

    // Shared with the manager's job-type filter so the two can't drift. This list
    // previously omitted PropertyManagementResidential, which seeded JN rules do
    // use — meaning those conditions couldn't be rebuilt in the UI.
    const projectTypes = PROJECT_TYPES.map(t => ({ value: t.key, label: t.label }));

    useEffect(() => {
        if (isOpen) {
            fetchUsers();
            fetchExistingTaskTitles();
            fetchEmailTemplates();
            fetchSendAsOptions();
            if (editingAutomation) {
                loadExistingAutomation();
            } else {
                resetAutomation();
            }
        }
    }, [isOpen, editingAutomation]);

    const fetchUsers = async () => {
        try {
            const response = await userAPI.getAll();
            setUsers(response.data);
        } catch (error) {
            console.error('Error fetching users:', error);
        }
    };

    const fetchExistingTaskTitles = async () => {
        try {
            const response = await taskAPI.getAll();
            const titles = [...new Set(response.data.map(t => t.title))].sort();
            setExistingTaskTitles(titles);
        } catch (error) {
            console.error('Error fetching tasks:', error);
        }
    };

    const fetchEmailTemplates = async () => {
        try {
            const response = await emailTemplatesAPI.getAll();
            setEmailTemplates(response.data);
        } catch (error) {
            console.error('Error fetching email templates:', error);
        }
    };

    const fetchSendAsOptions = async () => {
        try {
            const response = await emailAPI.getSendAsOptions();
            setSendAsOptions(response.data);
        } catch (error) {
            console.error('Error fetching send-as options:', error);
        }
    };

    const loadExistingAutomation = () => {
        // Parse triggerType from comma-separated string to array
        const triggerTypesArray = editingAutomation.triggerType 
            ? editingAutomation.triggerType.split(',').map(t => t.trim())
            : [];

        setAutomation({
            ...editingAutomation,
            triggerTypes: triggerTypesArray,
            triggerConditions: normalizeTriggerConditions(
                JSON.parse(editingAutomation.triggerConditions || '{}')
            ),
            actions: JSON.parse(editingAutomation.actions || '[]')
        });
    };

    const resetAutomation = () => {
        setAutomation({
            name: '',
            description: '',
            isActive: true,
            triggerTypes: [],
            triggerConditions: {},
            actions: [],
            delayDays: null,
            delayMinutes: null,
            triggerDateField: '',
            triggerDateOffsetMinutes: 0
        });
        setCurrentStep(1);
        setValidationErrors([]);
    };

    const toggleTriggerType = (typeValue) => {
        setAutomation(prev => {
            const currentTypes = prev.triggerTypes || [];
            const isSelected = currentTypes.includes(typeValue);
            
            let newTypes;
            if (isSelected) {
                newTypes = currentTypes.filter(t => t !== typeValue);
            } else {
                newTypes = [...currentTypes, typeValue];
            }

            // Clear conditions that don't apply to selected triggers
            let newConditions = { ...prev.triggerConditions };
            
            // Keep exclusions always
            const exclusions = {
                excludeProjectTypes: newConditions.excludeProjectTypes,
                excludeSalesRepIds: newConditions.excludeSalesRepIds
            };

            // Only keep relevant conditions
            newConditions = { ...exclusions };
            
            if (newTypes.includes('ProjectStatusChange') && prev.triggerConditions.toStatus) {
                newConditions.toStatus = prev.triggerConditions.toStatus;
                if (prev.triggerConditions.fromStatus) {
                    newConditions.fromStatus = prev.triggerConditions.fromStatus;
                }
            }
            
            if (newTypes.includes('TaskCompletion') && prev.triggerConditions.taskTitle) {
                newConditions.taskTitle = prev.triggerConditions.taskTitle;
            }

            return {
                ...prev,
                triggerTypes: newTypes,
                triggerConditions: newConditions
            };
        });
    };

    const updateTriggerCondition = (key, value) => {
        setAutomation(prev => ({
            ...prev,
            triggerConditions: {
                ...prev.triggerConditions,
                [key]: value
            }
        }));
    };

    const addAction = () => {
        const newAction = {
            type: 'CreateTask',
            title: '',
            description: '',
            priority: 'Medium',
            assignToSalesRep: true,
            assignToUserId: null,
            dueDays: null
        };

        setAutomation(prev => ({
            ...prev,
            actions: [...prev.actions, newAction]
        }));
    };

    const updateAction = (index, field, value) => {
        setAutomation(prev => ({
            ...prev,
            actions: prev.actions.map((action, i) => {
                if (i !== index) return action;
                if (field === 'type') {
                    // Reset to type-appropriate defaults when switching action type
                    const base = { type: value };
                    switch (value) {
                        case 'CreateTask':
                            return { ...base, title: '', description: '', priority: 'Medium', assignToSalesRep: true, assignToUserId: null, dueDays: null };
                        case 'UpdateProjectStatus':
                            return { ...base, newStatus: '' };
                        case 'SendEmail':
                            return { ...base, recipient: 'customer', explicitEmail: '', templateName: '', subject: '', htmlContent: '', fromAddress: '' };
                        case 'SendText':
                            return { ...base, message: '', recipient: 'customer' };
                        case 'AssignSalesRep':
                            return { ...base, userId: null };
                        default:
                            return base;
                    }
                }
                return { ...action, [field]: value };
            })
        }));
    };

    const updateActionAssignment = (index, value) => {
        setAutomation(prev => ({
            ...prev,
            actions: prev.actions.map((action, i) => {
                if (i !== index) return action;
                
                if (value === '' || value === 'salesrep') {
                    return {
                        ...action,
                        assignToSalesRep: true,
                        assignToUserId: null
                    };
                } else {
                    return {
                        ...action,
                        assignToSalesRep: false,
                        assignToUserId: parseInt(value)
                    };
                }
            })
        }));
    };

    const removeAction = (index) => {
        setAutomation(prev => ({
            ...prev,
            actions: prev.actions.filter((_, i) => i !== index)
        }));
    };

    const validateAutomation = () => {
        const errors = [];
        
        // Must have at least one trigger type
        if (!automation.triggerTypes || automation.triggerTypes.length === 0) {
            errors.push('Select at least one trigger type');
        }

        // Validate conditions based on selected trigger types
        if (automation.triggerTypes?.includes('ProjectStatusChange')) {
            // Either the dedicated dropdown or a newStatus condition in the tree
            // satisfies this — the backend's rule is "narrows on newStatus somewhere",
            // not "uses this particular field", and demanding the field would block
            // saving every rule normalised out of the top-level shape.
            if (!automation.triggerConditions.toStatus
                && !groupReferencesNewStatus(automation.triggerConditions.advancedFilters)) {
                errors.push('Status Change trigger must narrow on a status — pick one above, or add a "New Status" condition');
            }
        }
        
        if (automation.triggerTypes?.includes('TaskCompletion')) {
            if (!automation.triggerConditions.taskTitle || automation.triggerConditions.taskTitle.trim() === '') {
                errors.push('Task Completion trigger requires a task title');
            }
        }

        if (automation.triggerTypes?.includes('DateRelative') && !automation.triggerDateField) {
            errors.push('Date-Relative trigger requires a date field');
        }

        // Validate actions
        automation.actions.forEach((action, index) => {
            if (action.type === 'CreateTask') {
                if (!action.title || action.title.trim() === '') {
                    errors.push(`Action ${index + 1}: Task title is required`);
                }
                if (!action.assignToSalesRep && !action.assignToUserId) {
                    errors.push(`Action ${index + 1}: Task must be assigned to someone`);
                }
            }
            if (action.type === 'UpdateProjectStatus') {
                if (!action.newStatus) {
                    errors.push(`Action ${index + 1}: New status is required`);
                }
            }
            if (action.type === 'AssignSalesRep') {
                if (!action.userId) {
                    errors.push(`Action ${index + 1}: A user must be selected for sales rep assignment`);
                }
            }
        });

        return errors;
    };

    const handleSave = async () => {
        const errors = validateAutomation();
        if (errors.length > 0) {
            setValidationErrors(errors);
            setError('Please fix validation errors before saving');
            return;
        }

        setLoading(true);
        setError('');
        setValidationErrors([]);

        try {
            // Clean up empty exclusion arrays
            const cleanedConditions = { ...automation.triggerConditions };
            // Normalising happens on load, but a rule created before that could still
            // carry the top-level shape into a save. Writing both would leave the
            // backend preferring the top-level copy and silently ignoring the edit.
            TOP_LEVEL_GROUP_KEYS.forEach((k) => delete cleanedConditions[k]);
            if (cleanedConditions.excludeProjectTypes?.length === 0) {
                delete cleanedConditions.excludeProjectTypes;
            }
            if (cleanedConditions.excludeSalesRepIds?.length === 0) {
                delete cleanedConditions.excludeSalesRepIds;
            }
            // Drop empty advanced filters so we don't bloat the JSON on every save.
            const adv = cleanedConditions.advancedFilters;
            if (!adv || ((adv.conditions?.length ?? 0) === 0 && (adv.groups?.length ?? 0) === 0)) {
                delete cleanedConditions.advancedFilters;
            }

            // Convert triggerTypes array to comma-separated string
            const triggerTypeString = automation.triggerTypes.join(',');

            const automationData = {
                ...automation,
                triggerType: triggerTypeString, // Backend expects "triggerType" as string
                triggerConditions: JSON.stringify(cleanedConditions),
                actions: JSON.stringify(automation.actions),
                createdById: 1
            };

            // Remove the array version before sending
            delete automationData.triggerTypes;

            if (editingAutomation) {
                await automationAPI.update(editingAutomation.id, { ...automationData, id: editingAutomation.id });
            } else {
                await automationAPI.create(automationData);
            }

            onClose();
        } catch (error) {
            setError('Failed to save automation');
            console.error('Error saving automation:', error);
        } finally {
            setLoading(false);
        }
    };

    const getAssignmentValue = (action) => {
        if (action.assignToSalesRep) {
            return 'salesrep';
        } else if (action.assignToUserId) {
            return action.assignToUserId.toString();
        }
        return 'salesrep';
    };

    // Helper to check if a trigger type is selected
    const isTriggerSelected = (typeValue) => {
        return automation.triggerTypes?.includes(typeValue) || false;
    };

    // Date-relative offset: stored as signed minutes; presented as amount + unit + direction.
    const _offMin = automation.triggerDateOffsetMinutes ?? 0;
    const dateOffsetUnit = (_offMin !== 0 && _offMin % 1440 === 0) ? 'days' : 'minutes';
    const dateOffsetAmount = Math.abs(dateOffsetUnit === 'days' ? _offMin / 1440 : _offMin);
    const dateOffsetDir = _offMin < 0 ? 'before' : 'after';
    const setDateOffset = (amount, unit, dir) => {
        const mins = Math.abs(amount || 0) * (unit === 'days' ? 1440 : 1) * (dir === 'before' ? -1 : 1);
        setAutomation(prev => ({ ...prev, triggerDateOffsetMinutes: mins }));
    };
    const updateAutomationField = (field, value) => setAutomation(prev => ({ ...prev, [field]: value }));

    if (!isOpen) return null;

    return (
        <div className="automation-builder-overlay">
            <div className="automation-builder">
                
                {/* Header */}
                <div className="builder-header">
                    <div className="header-left">
                        <h2 className="builder-title">
                            <span className="title-icon">⚡</span>
                            {editingAutomation ? 'Edit Automation' : 'Create New Automation'}
                        </h2>
                        <div className="step-indicator">
                            <span className={`step ${currentStep >= 1 ? 'active' : ''}`}>1</span>
                            <span className={`step ${currentStep >= 2 ? 'active' : ''}`}>2</span>
                            <span className={`step ${currentStep >= 3 ? 'active' : ''}`}>3</span>
                        </div>
                    </div>
                    <button className="close-btn" onClick={onClose}>×</button>
                </div>

                {error && (
                    <div className="error-alert">
                        <span className="error-icon">⚠</span>
                        {error}
                    </div>
                )}

                {validationErrors.length > 0 && (
                    <div className="error-alert">
                        <span className="error-icon">⚠</span>
                        <ul style={{ margin: '8px 0 0 20px', padding: 0 }}>
                            {validationErrors.map((err, idx) => (
                                <li key={idx}>{err}</li>
                            ))}
                        </ul>
                    </div>
                )}

                <div className="builder-content">
                    
                    {/* Step 1: Basic Info */}
                    {currentStep === 1 && (
                        <div className="builder-step">
                            <div className="step-header">
                                <h3>AUTOMATION DETAILS</h3>
                                <p>Give your automation a name and description</p>
                            </div>
                            
                            <div className="form-grid">
                                <div className="form-group">
                                    <label className="form-label">Automation Name *</label>
                                    <input
                                        type="text"
                                        value={automation.name}
                                        onChange={(e) => setAutomation(prev => ({ ...prev, name: e.target.value }))}
                                        placeholder="e.g., Create Install Task on Contract Signed"
                                        className="form-input"
                                    />
                                </div>

                                <div className="form-group full-width">
                                    <label className="form-label">Description</label>
                                    <textarea
                                        value={automation.description}
                                        onChange={(e) => setAutomation(prev => ({ ...prev, description: e.target.value }))}
                                        placeholder="What does this automation do?"
                                        className="form-textarea"
                                        rows="3"
                                    />
                                </div>

                                <div className="form-group">
                                    <label className="toggle-label">
                                        <input
                                            type="checkbox"
                                            checked={automation.isActive}
                                            onChange={(e) => setAutomation(prev => ({ ...prev, isActive: e.target.checked }))}
                                        />
                                        <span className="toggle-switch"></span>
                                        Active on creation
                                    </label>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Step 2: Trigger Conditions */}
                    {currentStep === 2 && (
                        <div className="builder-step">
                            <div className="step-header">
                                <h3>TRIGGER CONDITIONS</h3>
                                <p>When should this automation fire? Select one or more triggers.</p>
                            </div>

                            <div className="conditions-builder">
                                {/* Trigger Type Selection - Multi-select */}
                                <div className="condition-group">
                                    <h4>TRIGGER TYPES</h4>
                                    <p className="condition-desc">Select all that apply - automation fires when ANY selected condition is met</p>
                                    <div className="trigger-type-selector">
                                        {triggerTypes.map(type => (
                                            <div 
                                                key={type.value}
                                                className={`trigger-type-card ${isTriggerSelected(type.value) ? 'selected' : ''}`}
                                                onClick={() => toggleTriggerType(type.value)}
                                            >
                                                <div className="trigger-checkbox">
                                                    <input
                                                        type="checkbox"
                                                        checked={isTriggerSelected(type.value)}
                                                        onChange={() => {}}
                                                        onClick={(e) => e.stopPropagation()}
                                                    />
                                                    <span className="checkbox-custom"></span>
                                                </div>
                                                <span className="trigger-icon">{type.icon}</span>
                                                <span className="trigger-label">{type.label}</span>
                                                <span className="trigger-desc">{type.description}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Selected triggers summary */}
                                {automation.triggerTypes?.length > 0 && (
                                    <div className="selected-triggers-summary">
                                        <span className="summary-label">Active triggers:</span>
                                        <div className="trigger-tags">
                                            {automation.triggerTypes.map(type => {
                                                const triggerInfo = triggerTypes.find(t => t.value === type);
                                                return (
                                                    <span key={type} className="trigger-tag">
                                                        {triggerInfo?.icon} {triggerInfo?.label}
                                                    </span>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}

                                {/* Project Status Change Conditions */}
                                {isTriggerSelected('ProjectStatusChange') && (
                                    <div className="condition-group highlight">
                                        <h4>🔄 STATUS CHANGE CONDITIONS</h4>
                                        <div className="condition-row">
                                            <span className="condition-text">When project status changes to:</span>
                                            <select
                                                value={automation.triggerConditions.toStatus || ''}
                                                onChange={(e) => updateTriggerCondition('toStatus', e.target.value)}
                                                className="condition-select"
                                            >
                                                <option value="">Select a status *</option>
                                                {projectStatuses.map(status => (
                                                    <option key={status.value} value={status.value}>
                                                        {status.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="condition-row">
                                            <span className="condition-text">From status (optional):</span>
                                            <select
                                                value={automation.triggerConditions.fromStatus || ''}
                                                onChange={(e) => updateTriggerCondition('fromStatus', e.target.value || undefined)}
                                                className="condition-select"
                                            >
                                                <option value="">Any status</option>
                                                {projectStatuses.map(status => (
                                                    <option key={status.value} value={status.value}>
                                                        {status.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                )}

                                {/* Task Completion Conditions */}
                                {isTriggerSelected('TaskCompletion') && (
                                    <div className="condition-group highlight">
                                        <h4>✅ TASK COMPLETION CONDITIONS</h4>
                                        <p className="condition-desc">Select which task triggers this automation when completed</p>
                                        <div className="condition-row">
                                            <span className="condition-text">When task is completed:</span>
                                            <div className="task-title-input-group">
                                                <input
                                                    type="text"
                                                    value={automation.triggerConditions.taskTitle || ''}
                                                    onChange={(e) => updateTriggerCondition('taskTitle', e.target.value)}
                                                    placeholder="Enter task title to match *"
                                                    className="condition-input"
                                                    list="existing-tasks"
                                                />
                                                <datalist id="existing-tasks">
                                                    {existingTaskTitles.map((title, idx) => (
                                                        <option key={idx} value={title} />
                                                    ))}
                                                </datalist>
                                            </div>
                                        </div>
                                        <p className="condition-hint">
                                            💡 Tip: The task title must match (case-insensitive). Choose from existing tasks above or type a new one.
                                        </p>
                                    </div>
                                )}

                                {/* Project Creation - No special conditions needed */}
                                {isTriggerSelected('ProjectCreation') && (
                                    <div className="condition-group highlight">
                                        <h4>📁 PROJECT CREATION</h4>
                                        <div className="info-box">
                                            <span className="info-icon">ℹ️</span>
                                            <span>This trigger fires whenever a new project is created. Use the exclusions below to limit which projects trigger this automation.</span>
                                        </div>
                                    </div>
                                )}

                                {/* Date-Relative timing config */}
                                {isTriggerSelected('DateRelative') && (
                                    <div className="condition-group highlight">
                                        <h4>📅 DATE-RELATIVE TIMING</h4>
                                        <div className="info-box">
                                            <span className="info-icon">ℹ️</span>
                                            <span>Fires relative to a job date. Add the job conditions (Type, Status, flags) under Advanced Filters below.</span>
                                        </div>
                                        <div className="condition-row">
                                            <label className="condition-label">Date field</label>
                                            <select
                                                className="condition-select"
                                                value={automation.triggerDateField || ''}
                                                onChange={(e) => updateAutomationField('triggerDateField', e.target.value)}
                                            >
                                                <option value="">Select a date…</option>
                                                {dateRelativeFields.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                                            </select>
                                        </div>
                                        <div className="condition-row" style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                                            <input
                                                type="number" min="0" className="condition-select" style={{ maxWidth: '90px' }}
                                                value={dateOffsetAmount}
                                                onChange={(e) => setDateOffset(parseInt(e.target.value || '0'), dateOffsetUnit, dateOffsetDir)}
                                            />
                                            <select className="condition-select" style={{ maxWidth: '130px' }}
                                                value={dateOffsetUnit}
                                                onChange={(e) => setDateOffset(dateOffsetAmount, e.target.value, dateOffsetDir)}>
                                                <option value="minutes">minutes</option>
                                                <option value="days">days</option>
                                            </select>
                                            <select className="condition-select" style={{ maxWidth: '130px' }}
                                                value={dateOffsetDir}
                                                onChange={(e) => setDateOffset(dateOffsetAmount, dateOffsetUnit, e.target.value)}>
                                                <option value="before">before</option>
                                                <option value="after">after</option>
                                            </select>
                                            <span>the selected date</span>
                                        </div>
                                    </div>
                                )}

                                {/* Common Exclusions - Available for all trigger types */}
                                <div className="condition-group">
                                    <h4>PROJECT TYPE EXCLUSIONS</h4>
                                    <p className="condition-desc">Don't trigger for these project types</p>
                                    <div className="checkbox-grid">
                                        {projectTypes.map(type => (
                                            <label key={type.value} className="checkbox-label">
                                                <input
                                                    type="checkbox"
                                                    checked={automation.triggerConditions.excludeProjectTypes?.includes(type.value) || false}
                                                    onChange={(e) => {
                                                        const current = automation.triggerConditions.excludeProjectTypes || [];
                                                        const updated = e.target.checked 
                                                            ? [...current, type.value]
                                                            : current.filter(t => t !== type.value);
                                                        updateTriggerCondition('excludeProjectTypes', updated);
                                                    }}
                                                />
                                                <span className="checkbox-custom"></span>
                                                Exclude {type.label}
                                            </label>
                                        ))}
                                    </div>
                                </div>

                                <div className="condition-group">
                                    <h4>SALES REP EXCLUSIONS</h4>
                                    <p className="condition-desc">Don't trigger for projects assigned to these sales reps</p>
                                    <div className="checkbox-grid">
                                        {users.filter(u => u.role === 1).map(user => (
                                            <label key={user.id} className="checkbox-label">
                                                <input
                                                    type="checkbox"
                                                    checked={automation.triggerConditions.excludeSalesRepIds?.includes(user.id) || false}
                                                    onChange={(e) => {
                                                        const current = automation.triggerConditions.excludeSalesRepIds || [];
                                                        const updated = e.target.checked
                                                            ? [...current, user.id]
                                                            : current.filter(id => id !== user.id);
                                                        updateTriggerCondition('excludeSalesRepIds', updated);
                                                    }}
                                                />
                                                <span className="checkbox-custom"></span>
                                                Exclude {user.username}
                                            </label>
                                        ))}
                                        {users.filter(u => u.role === 1).length === 0 && (
                                            <p className="no-data-text">No sales reps found</p>
                                        )}
                                    </div>
                                </div>

                                {/* Advanced AND/OR tree builder. Layered on top of the simple
                                    fields above — both apply together (AND). Use this when the
                                    simple fields aren't enough (e.g., complex Type+Status+Flag
                                    matrices that previously needed dozens of separate rules). */}
                                <div className="condition-group">
                                    <h4>ADVANCED FILTERS (AND / OR)</h4>
                                    <p className="condition-desc">
                                        Build nested AND/OR conditions on project + customer fields. Combines with the
                                        simple filters above using AND.
                                    </p>
                                    <ConditionTreeBuilder
                                        value={automation.triggerConditions.advancedFilters || blankGroup()}
                                        onChange={(next) => updateTriggerCondition('advancedFilters', next)}
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Step 3: Actions */}
                    {currentStep === 3 && (
                        <div className="builder-step">
                            <div className="step-header">
                                <h3>AUTOMATION ACTIONS</h3>
                                <p>What should happen when conditions are met?</p>
                            </div>

                            <div className="actions-builder">
                                {automation.actions.map((action, index) => (
                                    <div key={index} className="action-card">
                                        <div className="action-header">
                                            <select
                                                value={action.type}
                                                onChange={(e) => updateAction(index, 'type', e.target.value)}
                                                className="action-type-select"
                                            >
                                                <option value="CreateTask">Create Task</option>
                                                <option value="SendEmail">Send Email</option>
                                                <option value="SendText">Send Text</option>
                                                <option value="UpdateProjectStatus">Update Project Status</option>
                                                <option value="AssignSalesRep">Assign Sales Rep</option>
                                            </select>
                                            <button 
                                                className="remove-action-btn"
                                                onClick={() => removeAction(index)}
                                            >
                                                🗑
                                            </button>
                                        </div>

                                        {action.type === 'CreateTask' && (
                                            <div className="action-config">
                                                <div className="config-row">
                                                    <input
                                                        type="text"
                                                        placeholder="Task title *"
                                                        value={action.title}
                                                        onChange={(e) => updateAction(index, 'title', e.target.value)}
                                                        className="config-input"
                                                        required
                                                    />
                                                </div>
                                                <div className="config-row">
                                                    <textarea
                                                        placeholder="Task description"
                                                        value={action.description}
                                                        onChange={(e) => updateAction(index, 'description', e.target.value)}
                                                        className="config-textarea"
                                                        rows="2"
                                                    />
                                                </div>
                                                <div className="config-row three-col">
                                                    <select
                                                        value={action.priority}
                                                        onChange={(e) => updateAction(index, 'priority', e.target.value)}
                                                        className="config-select"
                                                    >
                                                        <option value="Low">Low Priority</option>
                                                        <option value="Medium">Medium Priority</option>
                                                        <option value="High">High Priority</option>
                                                        <option value="Urgent">Urgent Priority</option>
                                                    </select>
                                                    <select
                                                        value={getAssignmentValue(action)}
                                                        onChange={(e) => updateActionAssignment(index, e.target.value)}
                                                        className="config-select"
                                                    >
                                                        <option value="salesrep">Auto-assign to sales rep</option>
                                                        {users.map(user => (
                                                            <option key={user.id} value={user.id}>
                                                                Assign to {user.username}
                                                            </option>
                                                        ))}
                                                    </select>
                                                    <div className="due-days-input">
                                                        <input
                                                            type="number"
                                                            placeholder="Due"
                                                            value={action.dueDays || ''}
                                                            onChange={(e) => updateAction(index, 'dueDays', e.target.value ? parseInt(e.target.value) : null)}
                                                            className="config-input-small"
                                                            min="0"
                                                        />
                                                        <span className="input-suffix">days</span>
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {action.type === 'UpdateProjectStatus' && (
                                            <div className="action-config">
                                                <div className="config-row">
                                                    <label className="config-label">Change project status to:</label>
                                                    <select
                                                        value={action.newStatus || ''}
                                                        onChange={(e) => updateAction(index, 'newStatus', e.target.value)}
                                                        className="config-select"
                                                    >
                                                        <option value="">Select status *</option>
                                                        {projectStatuses.map(status => (
                                                            <option key={status.value} value={status.value}>
                                                                {status.label}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                            </div>
                                        )}

                                        {action.type === 'SendEmail' && (
                                            <div className="action-config">
                                                <div className="config-row">
                                                    <label className="config-label">Recipient</label>
                                                    <select
                                                        value={action.recipient || 'customer'}
                                                        onChange={(e) => updateAction(index, 'recipient', e.target.value)}
                                                        className="config-input"
                                                    >
                                                        <option value="customer">Customer</option>
                                                        <option value="salesRep">Sales Rep</option>
                                                        <option value="explicit">Specific email…</option>
                                                    </select>
                                                </div>
                                                <label className="checkbox-label inline">
                                                    <input
                                                        type="checkbox"
                                                        checked={action.sendToAllContacts || false}
                                                        onChange={(e) => updateAction(index, 'sendToAllContacts', e.target.checked)}
                                                    />
                                                    <span className="checkbox-custom"></span>
                                                    Send to all job contacts (everyone marked "receives updates" — overrides Recipient above)
                                                </label>
                                                {action.recipient === 'explicit' && !action.sendToAllContacts && (
                                                    <div className="config-row">
                                                        <input
                                                            type="email"
                                                            placeholder="recipient@example.com"
                                                            value={action.explicitEmail || ''}
                                                            onChange={(e) => updateAction(index, 'explicitEmail', e.target.value)}
                                                            className="config-input"
                                                        />
                                                    </div>
                                                )}
                                                <div className="config-row">
                                                    <label className="config-label">Email Template</label>
                                                    <select
                                                        value={action.templateName || ''}
                                                        onChange={(e) => updateAction(index, 'templateName', e.target.value)}
                                                        className="config-input"
                                                    >
                                                        <option value="">— Inline subject/body below —</option>
                                                        {emailTemplates.map(t => (
                                                            <option key={t.id} value={t.name}>{t.name}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                                {!action.templateName && (
                                                    <>
                                                        <div className="config-row">
                                                            <input
                                                                type="text"
                                                                placeholder="Subject (supports {{customerName}}, {{projectName}}, etc.)"
                                                                value={action.subject || ''}
                                                                onChange={(e) => updateAction(index, 'subject', e.target.value)}
                                                                className="config-input"
                                                            />
                                                        </div>
                                                        <div className="config-row">
                                                            <textarea
                                                                placeholder="HTML body (supports the same {{variables}})"
                                                                value={action.htmlContent || ''}
                                                                onChange={(e) => updateAction(index, 'htmlContent', e.target.value)}
                                                                className="config-textarea"
                                                                rows="4"
                                                            />
                                                        </div>
                                                    </>
                                                )}
                                                <div className="config-row">
                                                    <label className="config-label">From (optional)</label>
                                                    <select
                                                        value={action.fromAddress || ''}
                                                        onChange={(e) => updateAction(index, 'fromAddress', e.target.value)}
                                                        className="config-input"
                                                    >
                                                        <option value="">Sender's primary Gmail (default)</option>
                                                        {sendAsOptions.map(opt => (
                                                            <option key={opt.address} value={opt.address}>
                                                                {opt.label} ({opt.address})
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <p className="action-note">
                                                    📧 Sender = project's sales rep if they have a connected Gmail, else any admin with one. Variables: {`{{customerName}}, {{projectName}}, {{jobAddress}}, {{date}}, {{customerEmail}}, {{customerPhone}}, {{companyName}}, {{assignedUser}}, {{jobId}}, {{projectStatus}}`}
                                                </p>
                                            </div>
                                        )}

                                        {action.type === 'SendText' && (
                                            <div className="action-config">
                                                <div className="config-row">
                                                    <textarea
                                                        placeholder="Text message content"
                                                        value={action.message || ''}
                                                        onChange={(e) => updateAction(index, 'message', e.target.value)}
                                                        className="config-textarea"
                                                        rows="2"
                                                    />
                                                </div>
                                                <div className="config-row">
                                                    <label className="config-label">Send to:</label>
                                                    <select
                                                        // Seeded rules say "installer"; the backend treats it as a
                                                        // synonym for "crew", so show them as the same option rather
                                                        // than leaving the select blank on an existing rule.
                                                        value={action.recipient === 'installer' ? 'crew' : (action.recipient || 'customer')}
                                                        onChange={(e) => updateAction(index, 'recipient', e.target.value)}
                                                        className="config-select"
                                                    >
                                                        <option value="customer">Customer</option>
                                                        <option value="crew">Assigned Crew / Installer</option>
                                                        <option value="salesRep">Sales Rep</option>
                                                    </select>
                                                </div>
                                                {(action.recipient === 'crew' || action.recipient === 'installer') && (
                                                    <div className="config-row">
                                                        <label className="config-label">For section:</label>
                                                        <select
                                                            value={action.section || ''}
                                                            onChange={(e) => updateAction(index, 'section', e.target.value)}
                                                            className="config-select"
                                                        >
                                                            <option value="">Whole job</option>
                                                            {WORK_SECTIONS.map(s => (
                                                                <option key={s.key} value={s.key}>{s.label}</option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                )}
                                                <label className="checkbox-label inline">
                                                    <input
                                                        type="checkbox"
                                                        checked={action.sendToAllContacts || false}
                                                        onChange={(e) => updateAction(index, 'sendToAllContacts', e.target.checked)}
                                                    />
                                                    <span className="checkbox-custom"></span>
                                                    Send to all job contacts (everyone marked "receives updates" — overrides Send to above)
                                                </label>
                                                <label className="checkbox-label inline">
                                                    <input
                                                        type="checkbox"
                                                        checked={action.sendToAllCrews || false}
                                                        onChange={(e) => updateAction(index, 'sendToAllCrews', e.target.checked)}
                                                    />
                                                    <span className="checkbox-custom"></span>
                                                    Send to every crew on the job (each crew once, whatever section — overrides Send to above)
                                                </label>
                                                <p className="action-note">📱 Texts the selected recipient via Twilio and logs it to the message thread. Customer = the job's best number (mobile → main → work); Sales Rep = that staff member's phone. Assigned Crew / Installer = the crew lead's phone, or the installer's if the job is assigned to a person. Picking a section texts only that section's crew, falling back to the job's crew when it isn't split. Variables: {`{{JobPrimaryContactFirstName}}, {{projectName}}, {{jobAddress}}, {{JobDateStart}}, {{JobSalesRepName}}, {{JobSalesRepPhone}}, {{companyName}}, {{jobId}}, {{projectStatus}}`}</p>
                                            </div>
                                        )}

                                        {action.type === 'AssignSalesRep' && (
                                            <div className="action-config">
                                                <div className="config-row">
                                                    <label className="config-label">Assign sales rep:</label>
                                                    <select
                                                        value={action.userId || ''}
                                                        onChange={(e) => updateAction(index, 'userId', e.target.value ? parseInt(e.target.value) : null)}
                                                        className="config-select"
                                                    >
                                                        <option value="">Select a user *</option>
                                                        {users.map(user => (
                                                            <option key={user.id} value={user.id}>
                                                                {user.username}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))}

                                <button className="add-action-btn" onClick={addAction}>
                                    <span className="add-icon">+</span>
                                    Add Action
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="builder-footer">
                    <div className="footer-left">
                        {currentStep > 1 && (
                            <button 
                                className="btn-secondary"
                                onClick={() => setCurrentStep(prev => prev - 1)}
                            >
                                ← Previous
                            </button>
                        )}
                    </div>
                    
                    <div className="footer-right">
                        {currentStep < 3 ? (
                            <button 
                                className="btn-primary"
                                onClick={() => setCurrentStep(prev => prev + 1)}
                                disabled={currentStep === 1 && !automation.name.trim()}
                            >
                                Next →
                            </button>
                        ) : (
                            <button 
                                className="btn-create"
                                onClick={handleSave}
                                disabled={loading || automation.actions.length === 0}
                            >
                                {loading ? 'Saving...' : (editingAutomation ? 'Update Automation' : 'Create Automation')}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AutomationBuilder;